import React from 'react';
import { ExternalLink, ShoppingCart, CheckCircle, Clock, XCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

/**
 * AffiliateLinks Component
 * Displays affiliate shopping links for product monetization
 * @param {Object} props - Component props
 * @param {Array} props.affiliateLinks - Array of affiliate link objects
 * @param {string} props.productName - Product name for analytics
 */
const AffiliateLinks = ({ affiliateLinks, productName }) => {
  const getAvailabilityIcon = (availability) => {
    switch (availability) {
      case 'in_stock':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'low_stock':
        return <Clock className="w-4 h-4 text-warning" />;
      case 'out_of_stock':
        return <XCircle className="w-4 h-4 text-error" />;
      default:
        return null;
    }
  };

  const getAvailabilityText = (availability) => {
    switch (availability) {
      case 'in_stock':
        return 'In Stock';
      case 'low_stock':
        return 'Low Stock';
      case 'out_of_stock':
        return 'Out of Stock';
      default:
        return 'Unknown';
    }
  };

  const handleAffiliateClick = (retailer, url) => {
    // Track affiliate link clicks for analytics
    if (typeof window !== 'undefined' && window?.gtag) {
      window.gtag('event', 'affiliate_link_click', {
        event_category: 'engagement',
        event_label: `${productName} - ${retailer}`,
        value: 1,
      });
    }
    
    window?.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!affiliateLinks || affiliateLinks?.length === 0) {
    return null;
  }

  // Sort by price (lowest first) and availability
  const sortedLinks = [...affiliateLinks]?.sort((a, b) => {
    // Prioritize in-stock items
    if (a?.availability === 'in_stock' && b?.availability !== 'in_stock') return -1;
    if (a?.availability !== 'in_stock' && b?.availability === 'in_stock') return 1;
    // Then sort by price
    return a?.price - b?.price;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingCart className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-semibold text-gray-900">
          Where to Buy
        </h2>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Compare prices from trusted retailers. We may earn a commission from purchases made through these links.
      </p>
      <div className="space-y-3">
        {sortedLinks?.map((link, index) => (
          <div 
            key={index}
            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors duration-200"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-gray-900">
                  {link?.retailer}
                </h3>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  {getAvailabilityIcon(link?.availability)}
                  <span>{getAvailabilityText(link?.availability)}</span>
                </div>
              </div>
              <p className="text-2xl font-bold text-primary">
                ${link?.price?.toFixed(2)}
              </p>
            </div>

            <Button
              onClick={() => handleAffiliateClick(link?.retailer, link?.url)}
              disabled={link?.availability === 'out_of_stock'}
              className="ml-4"
              variant={link?.availability === 'out_of_stock' ? 'outline' : 'default'}
            >
              <span className="mr-2">Shop Now</span>
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Prices and availability are subject to change. Last updated: {new Date()?.toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default AffiliateLinks;